import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Moon, Sparkles, ArrowRight, MessageCircle } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";
import { useFestivals, festivalName, formatFestivalDate } from "../lib/festivals";
import ReminderButton from "./ReminderButton";

function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  return useMemo(() => {
    if (!target) return null;
    const diff = Math.max(0, new Date(target).getTime() - now);
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff / 3600000) % 24),
      minutes: Math.floor((diff / 60000) % 60),
      seconds: Math.floor((diff / 1000) % 60),
      done: diff === 0,
    };
  }, [target, now]);
}

function Unit({ value, label, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <span className="block font-serif text-3xl sm:text-4xl font-bold text-tara-ink tabular-nums stat-glow">
        {String(value).padStart(2, "0")}
      </span>
      <span className="block text-[10px] uppercase tracking-[0.18em] text-tara-muted mt-1">{label}</span>
    </div>
  );
}

export default function CountdownBanner({ variant = "section" }) {
  const { t, lang } = useLang();
  const { openBooking } = useBooking();
  const { items } = useFestivals(30);

  const major = items.find((f) => f.major) || items[0] || null;
  const targetIso = major ? `${major.date}T05:30:00+05:30` : null;
  const cd = useCountdown(targetIso);

  if (!major || !cd) return null;

  const name = festivalName(major, lang);

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={variant === "section" ? "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-16 sm:pb-20" : "mb-12"}
      data-testid="countdown-banner"
    >
      <div className="glass-liquid relative overflow-hidden rounded-[2rem] p-7 sm:p-10">
        <span className="orb orb-gold h-56 w-56 -top-16 -left-10" />
        <span className="orb orb-red h-52 w-52 -bottom-20 right-4" />
        <div className="pattern-sacred absolute inset-0 opacity-25" />

        <div className="relative flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">
          <div className="lg:flex-1">
            <span className="badge-gold inline-flex items-center gap-2 rounded-full px-3.5 py-1 text-[10px] font-serif uppercase tracking-[0.24em] mb-4">
              <Sparkles className="h-3 w-3" /> {t("countdown_eyebrow")}
            </span>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-tara-ink leading-tight mb-2">{name}</h2>
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-tara-body">
              <Moon className="h-4 w-4 text-tara-gold" />
              {formatFestivalDate(major.date)}
              {major.time_ist && <span className="text-tara-muted">· {major.time_ist}</span>}
            </p>
            <p className="text-sm text-tara-muted leading-relaxed mt-3 max-w-lg">{t("countdown_note")}</p>
          </div>

          <div className="flex items-center gap-5 sm:gap-8" data-testid="countdown-clock">
            <Unit value={cd.days} label={t("cd_days")} testid="countdown-days" />
            <span className="font-serif text-2xl text-tara-gold/50">:</span>
            <Unit value={cd.hours} label={t("cd_hours")} testid="countdown-hours" />
            <span className="font-serif text-2xl text-tara-gold/50">:</span>
            <Unit value={cd.minutes} label={t("cd_minutes")} testid="countdown-minutes" />
            <span className="font-serif text-2xl text-tara-gold/50 hidden sm:inline">:</span>
            <div className="hidden sm:block">
              <Unit value={cd.seconds} label={t("cd_seconds")} testid="countdown-seconds" />
            </div>
          </div>

          <div className="flex flex-col gap-3 lg:w-56">
            <button
              onClick={() => openBooking(major.service_id || "amavasya-puja", `${name} — ${formatFestivalDate(major.date)}`)}
              className="btn-sacred aura-pulse flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-serif font-semibold"
              data-testid="countdown-reserve-btn"
            >
              <MessageCircle className="h-4 w-4" /> {t("countdown_cta")}
            </button>
            <ReminderButton festival={major} compact />
            <Link
              to="/festivals"
              className="flex items-center justify-center gap-1.5 text-sm font-serif font-semibold text-tara-goldDeep hover:gap-3 transition-all"
              data-testid="countdown-calendar-link"
            >
              {t("view_all_dates")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
